import React, { useState, useEffect, useRef, useCallback, useMemo, Fragment } from "react";
import {
  Send,
  Plus,
  Paperclip,
  Share2,
  Settings,
  Menu,
  RotateCcw,
  GitBranch,
  Split,
  Square,
  X,
  Download,
  FileText,
  FileDown,
  FileSpreadsheet,
  Presentation,
  ChevronDown,
  AlertTriangle,
  BookOpen,
  Copy,
  Check,
  Loader2,
  Bot,
  Search,
  Wrench,
  Clock,
  Globe,
  Terminal,
  FilePen,
  FileSearch,
  FolderOpen,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeHighlight from "rehype-highlight";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import "highlight.js/styles/github-dark.min.css";

const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code || []), ["className"]],
    span: [...(defaultSchema.attributes?.span || []), ["className"]],
  },
};
import { useChat } from "@ai-sdk/react";
import { UIMessage } from "ai";
import { useNavigation } from "@/contexts/NavigationContext";
import { useGeneration } from "@/contexts/GenerationRegistry";
import { useModels } from "@/hooks/useModels";
import ModelSelectorModal from "@/components/modals/ModelSelectorModal";
import { ShareConversationModal } from "@/components/modals/ShareConversationModal";
import { ConfigPanel } from "@/components/ConfigPanel";
import { supabase, SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from 'sonner';
import { estimateTokens } from "@/lib/tokenEstimator";
import {
  notificationsSupported,
  getNotificationPermission,
  requestNotificationPermission,
} from "@/lib/notifications";
import { classifyClientError, friendlyProviderError, type ProviderErrorDetails } from "@/lib/providerErrors";
import { useConversationMessages, useCreateConversation, useForkConversation } from "@/hooks/useConversations";
import { useSummarizeAndFork } from "@/hooks/useSummarizeAndFork";
import { useConversationContextUsage } from "@/hooks/useConversationContextUsage";
import { ConversationContextIndicator } from "@/components/ConversationContextIndicator";
import { useAgents } from "@/hooks/useAgents";
import { useExportConversation } from "@/hooks/useExportConversation";
import { useProjectFiles } from "@/hooks/useProjectFiles";
import { useProject } from "@/hooks/useProject";
import { useAgentFiles } from "@/hooks/useAgentFiles";
import { useGlobalKnowledge } from "@/hooks/useGlobalKnowledge";
import { usePlatformSetting } from "@/hooks/usePlatformSettings";
import { useChatFiles, type ChatFileAttachment } from "@/hooks/useChatFiles";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChatImage } from "@/components/ChatImage";

import { SandboxArtifactsPanel } from "@/components/SandboxArtifactsPanel";
import { SandboxStatusBadge } from "@/components/SandboxStatusBadge";
import { HtmlPreviewPanel } from "@/components/HtmlPreviewPanel";
import { MarkdownPreviewPanel } from "@/components/MarkdownPreviewPanel";
import type { SidePreviewItem } from "@/components/SandboxArtifact";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";

const CHAT_ATTACHMENT_CONTENT_LIMIT = 4_000;
const CHAT_ATTACHMENT_LINE_LIMIT = 80;

// Mostra o priming de notificação no máximo uma vez por carga da página.
let notificationPrimeShown = false;

function buildChatAttachmentPreview(fileName: string, mimeType: string, contentText?: string): string {
  const text = String(contentText ?? "").trim();
  if (!text) return "";

  const lowerName = fileName.toLowerCase();
  const isStructuredData =
    mimeType.includes('csv') ||
    mimeType.includes('sheet') ||
    lowerName.endsWith('.csv') ||
    lowerName.endsWith('.xlsx') ||
    lowerName.endsWith('.xls');

  let preview = text;
  if (isStructuredData) {
    preview = text.split(/\r?\n/).slice(0, CHAT_ATTACHMENT_LINE_LIMIT).join('\n');
  }

  if (preview.length > CHAT_ATTACHMENT_CONTENT_LIMIT) {
    preview = `${preview.slice(0, CHAT_ATTACHMENT_CONTENT_LIMIT)}\n...[prévia truncada]`;
  }

  return preview;
}

/** Correct MIME type for files that browsers misidentify */
function correctMimeType(fileName: string, browserType: string): string {
  if (browserType && browserType !== 'application/octet-stream') return browserType;
  const ext = fileName.split('.').pop()?.toLowerCase();
  const map: Record<string, string> = {
    md: 'text/markdown', txt: 'text/plain', csv: 'text/csv',
    json: 'application/json', xml: 'application/xml',
    js: 'text/javascript', ts: 'text/typescript',
    py: 'text/x-python', html: 'text/html', css: 'text/css',
    yaml: 'text/yaml', yml: 'text/yaml', log: 'text/plain',
  };
  return map[ext || ''] || 'application/octet-stream';
}

/** Strip <user-attachments>...</user-attachments> block from text */
function stripAttachmentXml(text: string): string {
  return text.replace(/\n*<user-attachments>[\s\S]*?<\/user-attachments>\s*$/g, '').trim();
}

/** Extract all text from a UIMessage's parts */
function getMessageText(msg: UIMessage): string {
  const raw = msg.parts
    .filter((p): p is { type: "text"; text: string } => p.type === "text")
    .map((p) => p.text)
    .join("");
  return stripAttachmentXml(raw);
}

function extractGeneratedImageUrl(content: string | null | undefined): string | null {
  if (!content) return null;
  const match = content.match(/!\[[^\]]*\]\((https?:\/\/[^)]+)\)/);
  return match?.[1] ?? null;
}

/** Friendly display for tool invocation parts */
const TOOL_DISPLAY: Record<string, { icon: React.ReactNode; label: (args: any) => string }> = {
  getCurrentDateTime: {
    icon: <Clock className="w-3.5 h-3.5" />,
    label: () => "Verificando data e hora",
  },
  web_search: {
    icon: <Search className="w-3.5 h-3.5" />,
    label: (args: any) => args?.query ? `Pesquisando: "${args.query}"` : "Pesquisando na web",
  },
  searchWeb: {
    icon: <Search className="w-3.5 h-3.5" />,
    label: (args: any) => args?.query ? `Pesquisando: "${args.query}"` : "Pesquisando na web",
  },
  run_shell: {
    icon: <Terminal className="w-3.5 h-3.5" />,
    label: (args: any) => {
      const cmd = String(args?.command ?? "");
      return cmd.length > 50 ? cmd.slice(0, 50) + "…" : cmd || "Executando comando";
    },
  },
  write_file: {
    icon: <FilePen className="w-3.5 h-3.5" />,
    label: (args: any) => `Escrevendo \`${args?.path ?? ""}\``,
  },
  read_file: {
    icon: <FileSearch className="w-3.5 h-3.5" />,
    label: (args: any) => `Lendo \`${args?.path ?? ""}\``,
  },
  list_files: {
    icon: <FolderOpen className="w-3.5 h-3.5" />,
    label: () => "Listando arquivos",
  },
};

function getToolDisplay(toolName: string, args: any): { icon: React.ReactNode; label: string } {
  const entry = TOOL_DISPLAY[toolName];
  if (entry) return { icon: entry.icon, label: entry.label(args) };
  // Fallback: display tool name in a readable way
  const readable = toolName
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .trim();
  return {
    icon: <Wrench className="w-3.5 h-3.5" />,
    label: `Usando ${readable}${args?.query ? `: "${args.query}"` : ''}`,
  };
}

function inferLanguageFromPath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  const map: Record<string, string> = {
    py: 'python', js: 'javascript', ts: 'typescript', tsx: 'tsx', jsx: 'jsx',
    html: 'html', css: 'css', json: 'json', sh: 'bash', bash: 'bash',
    md: 'markdown', yml: 'yaml', yaml: 'yaml', xml: 'xml', sql: 'sql',
    rb: 'ruby', go: 'go', rs: 'rust', java: 'java', c: 'c', cpp: 'cpp',
    txt: 'text', csv: 'text',
  };
  return map[ext] ?? 'text';
}

function getImageExtensionFromMime(mimeType: string): string {
  const map: Record<string, string> = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/webp': 'webp',
  };
  return map[mimeType] ?? 'png';
}

function ToolInvocationDisplay({ toolName, args, state, result }: { toolName: string; args: any; state: string; result?: any }) {
  const { icon, label } = getToolDisplay(toolName, args);
  const isComplete = state === 'result';
  const [showDetails, setShowDetails] = useState(false);

  const hasDetails = isComplete && (toolName === 'run_shell' || toolName === 'write_file');

  return (
    <div className="py-1.5 text-xs text-tertiary">
      <div className="flex items-center gap-2">
        <span className="flex items-center justify-center w-5 h-5 rounded-full bg-secondary">
          {isComplete ? icon : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        </span>
        <span className={isComplete ? "line-through opacity-60" : ""}>{label}</span>
        {isComplete && <Check className="w-3 h-3 text-brand-primary" />}
        {hasDetails && (
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="ml-auto text-xs text-tertiary hover:text-primary transition-colors"
          >
            {showDetails ? "Ocultar" : "Ver detalhes"}
          </button>
        )}
      </div>

      {hasDetails && showDetails && toolName === 'run_shell' && (
        <div className="mt-2 ml-7">
          <Tabs defaultValue="command">
            <TabsList>
              <TabsTrigger value="command">Comando</TabsTrigger>
              <TabsTrigger value="output">Saída</TabsTrigger>
            </TabsList>
            <TabsContent value="command">
              <CodeBlock className="language-bash">{String(args?.command ?? "")}</CodeBlock>
            </TabsContent>
            <TabsContent value="output">
              {result?.exit_code !== undefined && result.exit_code !== 0 && (
                <div className="mb-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/30">
                    Erro (exit code {result.exit_code})
                  </span>
                </div>
              )}
              <pre className="bg-secondary p-3 rounded text-xs font-mono whitespace-pre-wrap overflow-x-auto max-h-[300px]">
                {result?.stdout || <span className="text-tertiary italic">(sem saída)</span>}
              </pre>
              {result?.stderr && (
                <pre className="mt-2 bg-secondary p-3 rounded text-xs font-mono whitespace-pre-wrap overflow-x-auto text-red-400 max-h-[300px]">
                  {result.stderr}
                </pre>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}

      {hasDetails && showDetails && toolName === 'write_file' && (
        <div className="mt-2 ml-7">
          <CodeBlock className={`language-${inferLanguageFromPath(String(args?.path ?? ""))}`}>
            {String(args?.content ?? "")}
          </CodeBlock>
        </div>
      )}

    </div>
  );
}

/** Custom code block with copy button and fade overlay */
function CodeBlock({ children, className, ...props }: React.HTMLAttributes<HTMLElement>) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const codeRef = useRef<HTMLElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const language = className?.replace("language-", "") || "";

  useEffect(() => {
    if (codeRef.current) {
      setIsOverflowing(codeRef.current.scrollHeight > 240);
    }
  }, [children]);

  const handleCopy = () => {
    const text = codeRef.current?.textContent ?? "";
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative rounded-lg border border-color overflow-hidden my-2 bg-secondary">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-color bg-hover">
        <span className="text-xs text-tertiary font-mono">{language || "código"}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-secondary hover:text-primary transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copiado" : "Copiar"}
        </button>
      </div>
      <div className={`relative ${!expanded && isOverflowing ? "max-h-[240px] overflow-hidden" : "overflow-auto"}`}>
        <code ref={codeRef} className={`block p-3 text-xs leading-relaxed whitespace-pre overflow-x-auto ${className ?? ""}`} {...props}>
          {children}
        </code>
        {!expanded && isOverflowing && (
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-secondary to-transparent" />
        )}
      </div>
      {isOverflowing && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full py-1.5 text-xs text-secondary hover:text-primary border-t border-color bg-hover transition-colors"
        >
          {expanded ? "Mostrar menos" : "Mostrar mais"}
        </button>
      )}
    </div>
  );
}

/** Markdown components override for rich rendering */
const markdownComponents = {
  pre: ({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) => {
    return <>{children}</>;
  },
  code: ({ children, className, ...props }: React.HTMLAttributes<HTMLElement>) => {
    const hasLanguage = className?.startsWith("language-") || className?.includes("hljs");
    const isMultiline = typeof children === "string" && children.includes("\n");
    const isBlock = hasLanguage || isMultiline;
    if (isBlock) {
      return <CodeBlock className={className} {...props}>{children}</CodeBlock>;
    }
    return <code className="px-1.5 py-0.5 rounded bg-secondary text-[0.85em] font-mono" {...props}>{children}</code>;
  },
  img: ({ src, alt, ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => {
    if (src) {
      return <ChatImage src={src} alt={alt || undefined} />;
    }
    return <img src={src} alt={alt} {...props} />;
  },
  h1: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h1 className="text-2xl font-bold mt-6 mb-3 pb-2 border-b border-color text-primary" {...props}>{children}</h1>
  ),
  h2: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h2 className="text-xl font-semibold mt-5 mb-2 pb-1.5 border-b border-color text-foreground" {...props}>{children}</h2>
  ),
  h3: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h3 className="text-base font-semibold mt-4 mb-2 text-primary" {...props}>{children}</h3>
  ),
  h4: ({ children, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
    <h4 className="text-sm font-semibold mt-3 mb-1.5 text-primary" {...props}>{children}</h4>
  ),
  p: ({ children, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className="my-3 leading-relaxed" {...props}>{children}</p>
  ),
  ul: ({ children, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul className="list-disc pl-6 my-3 space-y-1.5" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }: React.HTMLAttributes<HTMLOListElement>) => (
    <ol className="list-decimal pl-6 my-3 space-y-1.5" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }: React.HTMLAttributes<HTMLLIElement>) => (
    <li className="leading-relaxed" {...props}>{children}</li>
  ),
  blockquote: ({ children, ...props }: React.HTMLAttributes<HTMLQuoteElement>) => (
    <blockquote className="border-l-4 border-brand-primary pl-4 py-1 my-4 bg-muted/30 rounded-r-md italic text-secondary" {...props}>{children}</blockquote>
  ),
  a: ({ children, href, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-primary hover:underline" {...props}>{children}</a>
  ),
  hr: (props: React.HTMLAttributes<HTMLHRElement>) => (
    <hr className="my-5 border-color" {...props} />
  ),
  table: ({ children, ...props }: React.HTMLAttributes<HTMLTableElement>) => (
    <div className="my-4 overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-sm" {...props}>{children}</table>
    </div>
  ),
  thead: ({ children, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) => (
    <thead className="bg-secondary" {...props}>{children}</thead>
  ),
  th: ({ children, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => (
    <th className="px-3 py-2 text-left font-semibold text-primary border-b border-border" {...props}>{children}</th>
  ),
  td: ({ children, ...props }: React.HTMLAttributes<HTMLTableCellElement>) => (
    <td className="px-3 py-2 border-b border-border" {...props}>{children}</td>
  ),
  tr: ({ children, ...props }: React.HTMLAttributes<HTMLTableRowElement>) => (
    <tr className="hover:bg-muted/50 [&:last-child>td]:border-b-0" {...props}>{children}</tr>
  ),
  strong: ({ children, ...props }: React.HTMLAttributes<HTMLElement>) => (
    <strong className="font-semibold text-primary" {...props}>{children}</strong>
  ),
};

// Module-level plugin arrays (stable identity for memoization)
const REMARK_PLUGINS = [remarkGfm, remarkBreaks] as const;
const REHYPE_PLUGINS = [rehypeHighlight, [rehypeSanitize, sanitizeSchema]] as any;

function getAttachmentIcon(fileType: string) {
  switch (fileType) {
    case 'xlsx': case 'csv': return <FileSpreadsheet className="w-3.5 h-3.5 text-green-500" />;
    case 'pptx': return <Presentation className="w-3.5 h-3.5 text-orange-500" />;
    default: return <FileText className="w-3.5 h-3.5 text-blue-500" />;
  }
}

interface MessageBubbleProps {
  msg: UIMessage;
  isStreamingMsg: boolean;
  isFailed: boolean;
  msgFiles?: { file_name: string; file_type: string; storage_path: string }[];
  showInlineArtifacts: boolean;
  rangeStart?: string;
  rangeEnd?: string;
  conversationId?: string;
  userId?: string;
  copiedMsgId: string | null;
  forkPending: boolean;
  onPreview: (item: SidePreviewItem) => void;
  onCopy: (id: string, text: string) => void;
  onFork: (id: string) => void;
  onRetry: (id: string) => void;
  onRemove: (id: string) => void;
}

const MessageBubble = React.memo(function MessageBubble({
  msg,
  isStreamingMsg,
  isFailed,
  msgFiles,
  showInlineArtifacts,
  rangeStart,
  rangeEnd,
  conversationId,
  userId,
  copiedMsgId,
  forkPending,
  onPreview,
  onCopy,
  onFork,
  onRetry,
  onRemove,
}: MessageBubbleProps) {
  const text = getMessageText(msg);
  return (
    <Fragment>
      <div
        data-message-id={msg.id}
        className={`group/msg flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
      >
        <div className={`relative ${msg.role === "user" ? "max-w-[70%]" : "w-full"}`}>
          {msgFiles && msgFiles.length > 0 && (
            <div className={`flex flex-wrap gap-1.5 mb-1.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
              {msgFiles.map((f, i) => (
                <button
                  key={i}
                  onClick={async () => {
                    const { data } = await supabase.storage.from('chat-files').createSignedUrl(f.storage_path, 60);
                    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
                  }}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs bg-secondary/80 border border-color hover:bg-hover transition-colors"
                >
                  {getAttachmentIcon(f.file_type)}
                  <span className="truncate max-w-[120px] text-primary">{f.file_name}</span>
                  <Download className="w-3 h-3 text-tertiary" />
                </button>
              ))}
            </div>
          )}
          {msg.role === 'assistant' ? (
            (msg as any).parts?.map((p: any, i: number) => {
              const isV5TypedTool =
                typeof p.type === 'string' &&
                p.type.startsWith('tool-') &&
                p.type !== 'tool-invocation' &&
                p.type !== 'tool-call' &&
                p.type !== 'tool-result';
              const isDynamicTool = p.type === 'dynamic-tool';
              if (p.type === 'tool-invocation' || isV5TypedTool || isDynamicTool) {
                const ti = p.toolInvocation ?? {};
                const toolName =
                  ti.toolName ?? p.toolName ?? (isV5TypedTool ? p.type.slice(5) : 'unknown');
                const args = ti.args ?? p.input;
                const result = ti.result ?? p.output;
                const rawState = ti.state ?? p.state;
                const state =
                  rawState === 'output-available' || rawState === 'result' ? 'result' : 'call';
                const key = ti.toolCallId ?? p.toolCallId ?? `tool-${i}`;
                return (
                  <ToolInvocationDisplay
                    key={key}
                    toolName={toolName}
                    args={args}
                    state={state}
                    result={result}
                  />
                );
              }
              if (p.type === 'text' && p.text) {
                const partText = stripAttachmentXml(p.text).trim();
                if (!partText) return null;
                return (
                  <div key={`text-${i}`} className={`rounded-xl px-4 py-3 text-primary ${isFailed ? "opacity-70" : ""}`}>
                    <div style={{ fontFamily: "'Merriweather', Georgia, serif" }} className="text-[16px] max-w-none break-words overflow-hidden">
                      <ReactMarkdown
                        remarkPlugins={REMARK_PLUGINS as any}
                        rehypePlugins={REHYPE_PLUGINS}
                        components={markdownComponents}
                      >{partText}</ReactMarkdown>
                    </div>
                  </div>
                );
              }
              return null;
            })
          ) : (
            text && (
              <div className={`rounded-xl px-4 py-3 bg-muted text-primary rounded-br-sm ${isFailed ? "opacity-70" : ""}`}>
                <div style={{ fontFamily: "'Inter', system-ui, sans-serif" }} className="text-[16px] max-w-none break-words overflow-hidden prose prose-sm prose-p:my-1 prose-code:before:content-none prose-code:after:content-none prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-[0.85em] prose-code:bg-black/10 [&_*]:text-inherit">
                  <ReactMarkdown
                    remarkPlugins={REMARK_PLUGINS as any}
                    rehypePlugins={REHYPE_PLUGINS}
                  >{text}</ReactMarkdown>
                </div>
              </div>
            )
          )}
          {isFailed && (
            <div className="flex items-center justify-end gap-2 mt-1.5">
              <span className="flex items-center gap-1 text-xs text-destructive">
                <AlertTriangle className="w-3.5 h-3.5" />
                Falha ao gerar resposta
              </span>
              <button
                onClick={() => onRetry(msg.id)}
                className="flex items-center gap-1 text-xs text-secondary hover:text-primary transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Tentar novamente
              </button>
              <button
                onClick={() => onRemove(msg.id)}
                className="flex items-center gap-1 text-xs text-secondary hover:text-primary transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Remover
              </button>
            </div>
          )}
          {!isStreamingMsg && !isFailed && msg.role === 'assistant' && (
            <div className="flex gap-1 mt-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onCopy(msg.id, getMessageText(msg))}
                    className="p-1 rounded hover:bg-hover text-tertiary hover:text-primary transition-colors"
                  >
                    {copiedMsgId === msg.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{copiedMsgId === msg.id ? 'Copiado!' : 'Copiar'}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => onFork(msg.id)}
                    disabled={forkPending}
                    className="p-1 rounded hover:bg-hover text-tertiary hover:text-primary transition-colors"
                  >
                    <Split className="w-3.5 h-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Derivar conversa a partir daqui</TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
      {showInlineArtifacts && (
        <SandboxArtifactsPanel
          conversationId={conversationId}
          userId={userId}
          onPreview={onPreview}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          variant="inline"
        />
      )}
    </Fragment>
  );
});

export const ChatPage = () => {
  const { user } = useAuth();
  const {
    navigation,
    setConfigPanelOpen,
    sidebarCollapsed,
    setSidebarCollapsed,
    activeConversationId,
    setActiveConversationId,
    activeAgentId,
    setActiveAgentId,
    scrollToMessageId,
    setScrollToMessageId,
    setConversationHasMessages,
  } = useNavigation();
  
  const queryClient = useQueryClient();
  const { exportAsHTML, exportAsPDF } = useExportConversation();
  // renameConversation removed — title generation is now server-side

  const { data: llmModels } = useModels();
  const [selectedModel, setSelectedModel] = useState<string>("");
  const userOverrodeModel = useRef(false);

  // Set default model when models load
  useEffect(() => {
    if (!selectedModel && llmModels?.length) {
      setSelectedModel(llmModels[0].id);
    }
  }, [llmModels, selectedModel]);
  const [modelSelectorOpen, setModelSelectorOpen] = useState(false);
  const [modelSelectorModality, setModelSelectorModality] = useState<string | undefined>();
  const [fileCompatWarning, setFileCompatWarning] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [input, setInput] = useState("");
  const [failedMessageIds, setFailedMessageIds] = useState<Set<string>>(new Set());
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);
  // Lazy-load: render only the most recent N messages by default to keep
  // the input responsive on long conversations. Older messages are revealed
  // on demand via the "Mostrar mensagens anteriores" button.
  const INITIAL_VISIBLE = 15;
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  // Painel lateral de preview de artifact. Suporta 'html' (HtmlPreviewPanel
  // com iframe) e 'markdown' (MarkdownPreviewPanel com ReactMarkdown).
  // Imagem/PDF/CSV continuam abrindo em modal Dialog dentro do SandboxArtifact.
  const [activePreview, setActivePreview] = useState<SidePreviewItem | null>(null);
  // Check if current model supports vision (for image attachments)
  const currentModelCaps = useMemo(() => {
    const m = llmModels?.find(m => m.id === selectedModel);
    if (!m?.capabilities || typeof m.capabilities !== 'object') return {};
    return m.capabilities as Record<string, boolean>;
  }, [llmModels, selectedModel]);
  const modelSupportsVision = currentModelCaps.vision === true;
  const modelSupportsAudio = currentModelCaps.audio === true;

  const chatFiles = useChatFiles(modelSupportsVision, modelSupportsAudio);

  // Dynamic accept string for file input based on model capabilities
  const fileAcceptString = useMemo(() => {
    let accept = '.pdf,.docx,.pptx,.xlsx,.txt,.md,.csv';
    if (modelSupportsVision) accept += ',.png,.jpg,.jpeg,.gif,.webp';
    if (modelSupportsAudio) accept += ',.mp3,.wav,.m4a,.aac,.ogg,.webm,.flac';
    return accept;
  }, [modelSupportsVision, modelSupportsAudio]);

  // Listen for inject-prompt events from PromptSelectorModal
  useEffect(() => {
    const handler = (e: Event) => {
      const content = (e as CustomEvent<string>).detail;
      if (content) setInput(content);
    };
    window.addEventListener("inject-prompt", handler);
    return () => window.removeEventListener("inject-prompt", handler);
  }, []);


  // Agents from DB
  const { data: agents = [] } = useAgents(
    navigation.isPersonal ? "personal" : "global",
    undefined,
    navigation.projectId,
  );

  // Project files for context stuffing + native attachments
  const { data: projectFiles = [] } = useProjectFiles(navigation.projectId);
  const { data: currentProject } = useProject(navigation.projectId);
  const completedFiles = useMemo(
    () => projectFiles.filter((f) => f.parsing_status === "completed" && f.content_text),
    [projectFiles],
  );
  // All project files with ready/completed status (includes images/PDFs without content_text)
  const allReadyProjectFiles = useMemo(
    () => projectFiles.filter((f) => f.parsing_status === "completed" || f.parsing_status === "ready"),
    [projectFiles],
  );
  const projectFilesRef = useRef(allReadyProjectFiles);
  projectFilesRef.current = allReadyProjectFiles;
  const totalFileTokens = useMemo(
    () => completedFiles.reduce((sum, f) => sum + (f.token_count ?? 0), 0),
    [completedFiles],
  );
  const TOKEN_LIMIT_WARNING = 120_000;

  // Get selected agent's system prompt
  const activeAgent = useMemo(
    () => (activeAgentId ? agents.find((a) => a.id === activeAgentId) : undefined),
    [activeAgentId, agents],
  );

  // Sync model to agent's model when agent changes (unless user manually overrode)
  useEffect(() => {
    if (activeAgent?.model && llmModels?.some(m => m.id === activeAgent.model)) {
      userOverrodeModel.current = false;
      setSelectedModel(activeAgent.model);
    }
    if (!activeAgentId) {
      userOverrodeModel.current = false;
    }
  }, [activeAgentId, activeAgent?.model, llmModels]);
  // Fetch files attached to the active agent
  const { data: agentFilesData = [] } = useAgentFiles(activeAgentId ?? undefined);
  const agentFilesRef = useRef(agentFilesData);
  agentFilesRef.current = agentFilesData;

  // Espaço Caldeira: conhecimento global e instruções, herdados por toda
  // conversa que NÃO seja pessoal. Carregamento condicional pra evitar
  // ruído em conversas pessoais.
  const useGlobal = !navigation.isPersonal;
  const { data: globalFilesData = [] } = useGlobalKnowledge();
  const { data: globalSystemPromptValue } = usePlatformSetting<string>('global_system_prompt');
  const readyGlobalFiles = useMemo(
    () =>
      useGlobal
        ? globalFilesData.filter(
            (f) => f.parsing_status === 'completed' || f.parsing_status === 'ready',
          )
        : [],
    [useGlobal, globalFilesData],
  );
  const completedGlobalFiles = useMemo(
    () =>
      useGlobal
        ? globalFilesData.filter(
            (f) => f.parsing_status === 'completed' && f.content_text,
          )
        : [],
    [useGlobal, globalFilesData],
  );
  const globalFilesRef = useRef(readyGlobalFiles);
  globalFilesRef.current = readyGlobalFiles;
  const completedGlobalFilesRef = useRef(completedGlobalFiles);
  completedGlobalFilesRef.current = completedGlobalFiles;
  const globalSystemPromptRef = useRef<string | null>(null);
  globalSystemPromptRef.current = useGlobal ? (globalSystemPromptValue?.trim() || null) : null;

  const { data: dbMessages = [], isLoading: messagesLoading } = useConversationMessages(activeConversationId);

  // Mapa msg.id -> created_at do banco. Usado pra atrelar arquivos
  // gerados (sandbox_outputs) ao turno do assistant via janela temporal.
  // Precisa ficar ANTES de qualquer early return pra respeitar Rules of Hooks.
  const messageTimestamps = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of dbMessages as Array<{ id: string; created_at: string }>) {
      map.set(m.id, m.created_at);
    }
    return map;
  }, [dbMessages]);

  // Sync conversationHasMessages flag
  useEffect(() => {
    setConversationHasMessages(dbMessages.length > 0);
  }, [dbMessages.length, setConversationHasMessages]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputTextareaRef = useRef<HTMLTextAreaElement>(null);
  const isAtBottom = useRef(true);
  const wasStreaming = useRef(false);
  const isSubmittingRef = useRef(false);
  // Fallback timestamp pra janela de artefatos durante streaming, quando
  // mensagens ainda nao hidrataram do DB (ids locais do useChat sem created_at).
  const lastUserSubmitAtRef = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    isAtBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
  }, []);

  // Auto-grow textarea baseado no conteudo (max ~10 linhas)
  useEffect(() => {
    const ta = inputTextareaRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 240)}px`;
  }, [input]);



  // Chat file attachments linked to this conversation
  const [conversationFiles, setConversationFiles] = useState<Record<string, { file_name: string; file_type: string; storage_path: string }[]>>({});
  const [filesFetchKey, setFilesFetchKey] = useState(0);
  const refreshConversationFiles = useCallback(() => setFilesFetchKey(k => k + 1), []);

  // Mutations
  const createConversation = useCreateConversation();
  const forkConversation = useForkConversation();
  const summarizeAndFork = useSummarizeAndFork();

  // Fetch active conversation for fork info
  const [forkedFromTitle, setForkedFromTitle] = useState<string | null>(null);
  const [forkedFromId, setForkedFromId] = useState<string | null>(null);
  const [forkedFromOwnerName, setForkedFromOwnerName] = useState<string | null>(null);
  const [forkedFromAccessible, setForkedFromAccessible] = useState<boolean>(false);

  // Refs for transport to read latest values without re-creating
  const selectedModelRef = useRef(selectedModel);
  selectedModelRef.current = selectedModel;
  const activeAgentRef = useRef(activeAgent);
  activeAgentRef.current = activeAgent;
  const completedFilesRef = useRef(completedFiles);
  completedFilesRef.current = completedFiles;
  const projectInstructionsRef = useRef<string | null>(null);
  projectInstructionsRef.current = currentProject?.instructions ?? null;
  // Holds native file base64 data for the current send
  const nativeFilesRef = useRef<Array<{ fileName: string; mimeType: string; base64Data: string }>>([]);
  // Ref for model capabilities (for tool calling)
  const modelsRef = useRef(llmModels);
  modelsRef.current = llmModels;
  // Guard: skip DB→useChat sync while we're actively sending a message
  const skipSyncRef = useRef(false);
  // Refs for server-side persistence: the transport body() reads these
  const userMessageRef = useRef<string | undefined>();
  const chatFileRecordsRef = useRef<Array<{
    file_name: string; file_type: string; file_size_bytes: number;
    mime_type: string; storage_path: string; content_text?: string; token_count?: number;
  }> | undefined>();

  // Active conversation ref so callbacks can read it without stale closures
  const activeConvRef = useRef(activeConversationId);
  activeConvRef.current = activeConversationId;

  // Monta o payload (body) enviado ao edge function `chat`, capturando os
  // settings (modelo/agente/prompt/arquivos) no momento do envio. Os settings
  // ficam FIXOS na Geração (ver docs/adr/0001). Lê dos refs (sempre atualizados)
  // e consome os refs de envio (userMessage/nativeFiles/chatFileRecords) uma vez.
  const buildSendBody = useCallback((convId: string) => {
        const currentModel = modelsRef.current?.find(m => m.id === selectedModelRef.current);
        const capsTools = currentModel?.capabilities && typeof currentModel.capabilities === 'object'
          ? (currentModel.capabilities as Record<string, unknown>).tools === true
          : false;
        // Defesa em runtime: espelha o fallback do edge function `chat`.
        // Garante que famílias com tool calling (Claude, GPT, Gemini, Grok,
        // DeepSeek) ativem tools mesmo se o OpenRouter não anunciar.
        const TOOL_CAPABLE_FAMILIES = [
          /^anthropic\/claude-/i,
          /^openai\/(gpt-(3\.5|4|5)|o\d)/i,
          /^google\/gemini-(1\.5|2|2\.5|3|3\.\d)/i,
          /^x-ai\/grok-/i,
          /^deepseek\//i,
        ];
        const modelId = currentModel?.id ?? selectedModelRef.current ?? "";
        const supportsTools = capsTools || TOOL_CAPABLE_FAMILIES.some((re) => re.test(modelId));
        const agentPrompt = activeAgentRef.current?.system_prompt ?? "";
        const files = completedFilesRef.current;
        const readyProjectFiles = projectFilesRef.current;
        const readyAgentFiles = agentFilesRef.current.filter(
          (f) => f.parsing_status === 'completed' || f.parsing_status === 'ready'
        );
        const projectInstructions = projectInstructionsRef.current?.trim();
        const globalInstructions = globalSystemPromptRef.current;
        const globalCompletedFiles = completedGlobalFilesRef.current;
        const readyGlobals = globalFilesRef.current;
        let systemPrompt = agentPrompt;

        // Conhecimento e instrucoes globais do Espaco Caldeira sao prefixados
        // antes de qualquer outro contexto. So aplicam em conversas nao-pessoais.
        if (globalInstructions) {
          const globalBlock = `<global-instructions>\n${globalInstructions}\n</global-instructions>`;
          systemPrompt = systemPrompt ? `${globalBlock}\n\n${systemPrompt}` : globalBlock;
        }
        // Conhecimento global e SEMPRE inline no system prompt, mesmo com
        // modelos que suportam tools. Justificativa: o teto de 50k tokens
        // mantem o conteudo pequeno por design, entao injetar direto e mais
        // barato e previsivel do que tool calls (sem ruido de "Listando
        // arquivos" / "Lendo arquivo" antes da resposta).
        if (globalCompletedFiles.length > 0) {
          const docs = globalCompletedFiles
            .map((f) => `<document name="${f.file_name}" type="${f.file_type}">\n${f.content_text}\n</document>`)
            .join('\n\n');
          const globalKnowledgeBlock = `<global-knowledge>\nVocê tem acesso à base de conhecimento institucional do Caldeira. Use estas informações como contexto quando relevante. Cite o nome do arquivo fonte quando usar.\n\n${docs}\n</global-knowledge>`;
          systemPrompt = systemPrompt ? `${globalKnowledgeBlock}\n\n${systemPrompt}` : globalKnowledgeBlock;
        }

        if (projectInstructions) {
          const projectBlock = `<project-instructions>\n${projectInstructions}\n</project-instructions>`;
          systemPrompt = systemPrompt ? `${projectBlock}\n\n${systemPrompt}` : projectBlock;
        }

        if (files.length > 0) {
          if (supportsTools) {
            const inventory = readyProjectFiles
              .map((f) => `- ${f.file_name} (${f.file_type})`)
              .join("\n");
            const knowledgeBlock = `\n\n<knowledge-base mode="sandbox-reference">\nA base de conhecimento do projeto está disponível na sandbox. Consulte os arquivos em /home/sprite/uploads/ quando precisar do conteúdo completo.\n\n${inventory}\n</knowledge-base>`;
            systemPrompt = (systemPrompt || "") + knowledgeBlock;
          } else {
            const docs = files
              .map((f) => `<document name="${f.file_name}" type="${f.file_type}">\n${f.content_text}\n</document>`)
              .join("\n\n");
            const knowledgeBlock = `\n\n<knowledge-base>\nVocê tem acesso à base de conhecimento do projeto. Use as informações dos documentos abaixo para responder perguntas. Sempre cite o nome do arquivo fonte quando usar informações da base.\n\n${docs}\n</knowledge-base>`;
            systemPrompt = (systemPrompt || "") + knowledgeBlock;
          }
        }

        if (readyAgentFiles.length > 0) {
          if (supportsTools) {
            const inventory = readyAgentFiles
              .map((f) => `- ${f.file_name} (${f.file_type})`)
              .join('\n');
            const agentContext = `<agent-knowledge mode="sandbox-reference">\nArquivos vinculados ao agente estão disponíveis na sandbox em /home/sprite/uploads/.\n\n${inventory}\n</agent-knowledge>`;
            systemPrompt = systemPrompt
              ? `${systemPrompt}\n\n${agentContext}`
              : agentContext;
          } else {
            const textContextParts = readyAgentFiles
              .filter((f) => !!f.content_text)
              .map((f) => `<agent-file name="${f.file_name}">\n${f.content_text}\n</agent-file>`);

            if (textContextParts.length > 0) {
              const agentContext = `<agent-knowledge>\n${textContextParts.join('\n')}\n</agent-knowledge>`;
              systemPrompt = systemPrompt
                ? `${systemPrompt}\n\n${agentContext}`
                : agentContext;
            }
          }
        }

        const nf = [...nativeFilesRef.current];
        nativeFilesRef.current = []; // consume chat attachments once

        const modelCapabilities = currentModel?.capabilities && typeof currentModel.capabilities === 'object'
          ? currentModel.capabilities as Record<string, boolean>
          : undefined;
        // Grab and clear server-side persistence refs
        const um = userMessageRef.current;
        const cfr = chatFileRecordsRef.current;
        userMessageRef.current = undefined;
        chatFileRecordsRef.current = undefined;

        return {
          model: selectedModelRef.current,
          conversationId: convId,
          ...(systemPrompt ? { systemPrompt } : {}),
          ...(nf.length > 0 ? { nativeFiles: nf } : {}),
          ...(modelCapabilities ? { modelCapabilities } : {}),
          ...(um ? { userMessage: um } : {}),
          ...(cfr && cfr.length > 0 ? { chatFileRecords: cfr } : {}),
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Registry de gerações concorrentes. O Chat desta conversa vive no registry
  // (sobrevive à troca de conversa/aba); esta página é só a VIEW. O ChatPage é
  // chaveado por conversationId no MainContent, então cada montagem é de UMA
  // conversa — o vazamento de stream fica impossível por construção.
  const generation = useGeneration();
  const boundConvId = activeConversationId ?? "__new__";
  const chat = useMemo(
    () => generation.ensureTextChat(boundConvId),
    [generation, boundConvId],
  );
  const {
    messages: chatMessages,
    sendMessage,
    status,
    stop,
    setMessages,
    error: chatError,
  } = useChat({ chat });

  // Status de geração de imagem desta conversa (vem do registry).
  const imageGenStatus = generation.imageStatusFor(boundConvId) ?? "Gerando imagem...";
  const isGeneratingImage = !!generation.imageStatusFor(boundConvId);

  // Reabilita o sync do DB quando a Geração termina (ready/error).
  useEffect(() => {
    if (status === "ready" || status === "error") {
      skipSyncRef.current = false;
    }
  }, [status]);

  // Erro da Geração ATIVA: marca a última mensagem do user como falha + toast
  // detalhado (com label do modelo). Gerações em segundo plano têm marcador e
  // notificação tratados pelo GenerationRegistry.
  const handledErrorRef = useRef<Error | null>(null);
  useEffect(() => {
    if (status !== "error" || !chatError) return;
    if (handledErrorRef.current === chatError) return;
    handledErrorRef.current = chatError;
    const lastUserMsg = [...chatMessages].reverse().find((m) => m.role === "user");
    if (lastUserMsg?.id) {
      setFailedMessageIds((prev) => new Set(prev).add(lastUserMsg.id));
    }
    const msg = chatError.message || "";
    const kind = classifyClientError(msg);
    const modelLabel = llmModels?.find((m) => m.id === selectedModel)?.display_name;
    const { title, description } = friendlyProviderError({ kind, providerMessage: msg }, modelLabel);
    toast.error(title, { description, duration: 9000 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, chatError]);

  // Ciclo de vida da view (ChatPage é chaveado por conversa): ao abrir, limpa o
  // marcador "resposta nova"; ao desmontar (trocar de conversa), libera a view
  // e o registry evicta o Chat se a conversa estiver idle (não-gerando).
  useEffect(() => {
    if (activeConversationId) generation.clearUnread(activeConversationId);
    return () => {
      generation.releaseView(boundConvId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isStreamingBase =
    status === "streaming" ||
    status === "submitted" ||
    isGeneratingImage ||
    (activeConversationId ? generation.isGenerating(activeConversationId) : false);

  // Check if current model is an image generation model
  const isImageGenModel = useMemo(() => {
    if (!selectedModel || !llmModels) return false;
    const model = llmModels.find(m => m.id === selectedModel);
    if (!model?.capabilities || typeof model.capabilities !== 'object') return false;
    return (model.capabilities as Record<string, unknown>).image_gen === true;
  }, [selectedModel, llmModels]);

  // Fetch chat_files for the conversation (re-fetches when filesFetchKey changes)
  useEffect(() => {
    if (!activeConversationId) {
      setConversationFiles({});
      return;
    }
    const fetchFiles = async () => {
      const { data } = await supabase
        .from('chat_files')
        .select('message_id, file_name, file_type, storage_path')
        .eq('conversation_id', activeConversationId);
      if (data) {
        const grouped: Record<string, { file_name: string; file_type: string; storage_path: string }[]> = {};
        for (const f of data) {
          if (!f.message_id) continue;
          if (!grouped[f.message_id]) grouped[f.message_id] = [];
          grouped[f.message_id].push({ file_name: f.file_name, file_type: f.file_type, storage_path: f.storage_path });
        }
        setConversationFiles(grouped);
      }
    };
    fetchFiles();
  }, [activeConversationId, filesFetchKey]);

  // Fetch conversation metadata for export + detect server-side generation
  const [convTitle, setConvTitle] = useState("Nova conversa");
  const [convCreatedAt, setConvCreatedAt] = useState<string | undefined>();
  const [isServerGenerating, setIsServerGenerating] = useState(false);
  const serverPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!activeConversationId) {
      setConvTitle("Nova conversa");
      setConvCreatedAt(undefined);
      setIsServerGenerating(false);
      if (serverPollRef.current) { clearInterval(serverPollRef.current); serverPollRef.current = null; }
      return;
    }
    // Threshold para considerar "generating" como zumbi (edge function morreu sem cleanup).
    // Edge functions Pro tem timeout maximo de ~400s (6.7min), entao 10min cobre com folga.
    const ZOMBIE_GENERATION_MS = 10 * 60 * 1000;

    const resetZombieStatus = async (convId: string) => {
      console.warn("[chat] Detected zombie generation_status, forcing reset:", convId);
      await supabase
        .from("conversations")
        .update({ generation_status: "idle" } as any)
        .eq("id", convId);
      setIsServerGenerating(false);
      queryClient.invalidateQueries({ queryKey: ["messages", convId] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    };

    supabase
      .from("conversations")
      .select("title, created_at, model, agent_id, generation_status, updated_at")
      .eq("id", activeConversationId)
      .single()
      .then(async ({ data }) => {
        if (data) {
          setConvTitle(data.title);
          setConvCreatedAt(data.created_at);
          if (data.model) setSelectedModel(data.model);
          setActiveAgentId(data.agent_id ?? null);

          // If server is still generating, start polling for completion
          if ((data as any).generation_status === "generating") {
            // Zombie check: status preso ha mais de 10min indica edge function morta sem cleanup
            const updatedAt = new Date((data as any).updated_at).getTime();
            if (Date.now() - updatedAt > ZOMBIE_GENERATION_MS) {
              await resetZombieStatus(activeConversationId);
              return;
            }

            setIsServerGenerating(true);
            const poll = setInterval(async () => {
              const { data: updated } = await supabase
                .from("conversations")
                .select("generation_status, updated_at")
                .eq("id", activeConversationId)
                .single();
              if (!updated || (updated as any).generation_status !== "generating") {
                setIsServerGenerating(false);
                clearInterval(poll);
                serverPollRef.current = null;
                // Refetch messages to pick up the completed assistant response
                queryClient.invalidateQueries({ queryKey: ["messages", activeConversationId] });
                queryClient.invalidateQueries({ queryKey: ["conversations"] });
                return;
              }
              // Zombie check tambem durante polling: se updated_at parou ha 10min, força reset
              const ts = new Date((updated as any).updated_at).getTime();
              if (Date.now() - ts > ZOMBIE_GENERATION_MS) {
                clearInterval(poll);
                serverPollRef.current = null;
                await resetZombieStatus(activeConversationId);
              }
            }, 3000);
            serverPollRef.current = poll;
          } else {
            setIsServerGenerating(false);
          }
        }
      });

    return () => {
      if (serverPollRef.current) { clearInterval(serverPollRef.current); serverPollRef.current = null; }
    };
  }, [activeConversationId, queryClient]);

  const isStreaming = isStreamingBase || isServerGenerating;

  // Libera o lock de envio quando o stream comeca (a partir dali, isStreaming protege)
  useEffect(() => {
    if (isStreaming) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [isStreaming]);

  const recoverImageGenerationFromDb = useCallback(async (conversationId: string) => {
    const readLatestAssistant = async () => {
      const [{ data: conv }, { data: latestAssistant }] = await Promise.all([
        supabase
          .from("conversations")
          .select("generation_status")
          .eq("id", conversationId)
          .single(),
        supabase
          .from("messages")
          .select("content")
          .eq("conversation_id", conversationId)
          .eq("role", "assistant")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      return {
        generationStatus: (conv as any)?.generation_status as string | undefined,
        content: latestAssistant?.content ?? null,
      };
    };

    let snapshot = await readLatestAssistant();
    let recoveredUrl = extractGeneratedImageUrl(snapshot.content);
    if (recoveredUrl) return recoveredUrl;

    const delays = [2000, 4000, 8000, 12000];
    for (const delay of delays) {
      if (!snapshot.generationStatus || snapshot.generationStatus !== "generating") break;
      await new Promise((resolve) => setTimeout(resolve, delay));
      snapshot = await readLatestAssistant();
      recoveredUrl = extractGeneratedImageUrl(snapshot.content);
      if (recoveredUrl) return recoveredUrl;
    }

    return null;
  }, []);

  const handleExport = useCallback(
    (format: "html" | "pdf") => {
      const msgs = dbMessages.map((m) => ({ role: m.role, content: m.content, created_at: m.created_at }));
      const opts = { title: convTitle, model: selectedModel, messages: msgs, createdAt: convCreatedAt };
      if (format === "html") exportAsHTML(opts);
      else exportAsPDF(opts);
      toast.success("Conversa exportada com sucesso");
    },
    [dbMessages, convTitle, selectedModel, convCreatedAt, exportAsHTML, exportAsPDF],
  );

  const canExport = !!activeConversationId && dbMessages.length > 0 && !isStreaming;
  const canShare = canExport;
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Sync DB messages into useChat when conversation changes
  const lastSyncedConvId = useRef<string | null>(null);
  const lastSyncedMsgCount = useRef<number>(0);
  useEffect(() => {
    if (!activeConversationId) {
      if (lastSyncedConvId.current !== null) {
        setMessages([]);
        lastSyncedConvId.current = null;
        lastSyncedMsgCount.current = 0;
      }
      return;
    }
    if (!messagesLoading && dbMessages) {
      const convChanged = lastSyncedConvId.current !== activeConversationId;
      const msgCountChanged = lastSyncedMsgCount.current !== dbMessages.length;
      // "Sessão vence": enquanto a conversa gera (mesmo em background, ao
      // voltar nela), o Chat vivo é a fonte de verdade — não sobrescreve do DB.
      const isBusy =
        skipSyncRef.current ||
        status === "streaming" ||
        status === "submitted" ||
        generation.isGenerating(activeConversationId);
      // Only force-sync when switching FROM one conversation TO another (not null→new)
      const isRealSwitch = convChanged && lastSyncedConvId.current !== null;
      // Race guard pro primeiro turno de conversa nova (lazy creation):
      // useChat ja tem user+assistant em memoria quando status vira "ready"
      // e skipSyncRef cai pra false no onFinish do client. Mas a edge function
      // chat ainda nao persistiu (onFinish server-side e parcialmente paralelo,
      // e o React Query nao invalidou+refetched). dbMessages=[] enquanto
      // chatMessages=[user, assistant]. Sem essa guarda, o ramo
      // "convChanged && !isBusy" disparava setMessages(dbMessages.map(...))
      // com array vazio, zerando a UI por ~100-500ms ate o refetch chegar
      // (em conexao lenta, dura mais e o user le como "sumiu, perdi a resposta").
      // isRealSwitch (troca explicita de conversa) continua passando: nesse
      // caso o local pode ter mensagens da conv anterior e sobrescrever com
      // o historico da nova conv e o comportamento desejado.
      const localAheadOfDb = chatMessages.length > dbMessages.length;
      const willSync =
        isRealSwitch ||
        (!localAheadOfDb && ((convChanged && !isBusy) || (msgCountChanged && !isBusy)));
      if (willSync) {
        if (isRealSwitch) {
          // Don't call stop() here — aborting the HTTP connection prevents
          // the edge function's onFinish from persisting the assistant message.
          // Instead, just reset local state; the stream will finish in the
          // background and save the response server-side.
          skipSyncRef.current = false;
          setFailedMessageIds(new Set());
        }
        lastSyncedConvId.current = activeConversationId;
        lastSyncedMsgCount.current = dbMessages.length;
        setMessages(
          dbMessages.map((m) => {
            let parts: any[] = [];
            const meta = (m as any).metadata;
            // Prefer ordered parts from new format
            if (meta?.parts && Array.isArray(meta.parts)) {
              parts = meta.parts.map((p: any) => {
                if (p.type === "tool-invocation") {
                  return {
                    type: "tool-invocation" as const,
                    toolInvocation: {
                      toolCallId: p.toolCallId,
                      toolName: p.toolName,
                      args: p.args,
                      result: p.result,
                      state: "result" as const,
                    },
                  };
                }
                return { type: "text" as const, text: p.text };
              });
            } else {
              // Legacy fallback: tools before text
              if (meta?.toolInvocations && Array.isArray(meta.toolInvocations)) {
                for (const ti of meta.toolInvocations) {
                  parts.push({
                    type: "tool-invocation" as const,
                    toolInvocation: {
                      toolCallId: ti.toolCallId,
                      toolName: ti.toolName,
                      args: ti.args,
                      result: ti.result,
                      state: "result" as const,
                    },
                  });
                }
              }
              parts.push({ type: "text" as const, text: m.content });
            }
            return {
              id: m.id,
              role: m.role as "user" | "assistant",
              parts,
            };
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, dbMessages, messagesLoading, status]);

  // Fork info — reads snapshot fields populated at fork time so the banner works
  // even when the parent conversation belongs to another user (RLS-blocked).
  useEffect(() => {
    if (!activeConversationId) {
      setForkedFromId(null);
      setForkedFromTitle(null);
      setForkedFromOwnerName(null);
      setForkedFromAccessible(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("conversations")
        .select("forked_from_conversation_id, forked_from_title, forked_from_owner_name")
        .eq("id", activeConversationId)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error("[fork-banner] failed to load conversation", error);
        return;
      }
      const forkId = (data as any)?.forked_from_conversation_id ?? null;
      const snapshotTitle = (data as any)?.forked_from_title ?? null;
      const ownerName = (data as any)?.forked_from_owner_name ?? null;
      setForkedFromId(forkId);
      setForkedFromOwnerName(ownerName);
      setForkedFromTitle(snapshotTitle && snapshotTitle.trim() ? snapshotTitle : (forkId ? "Conversa sem título" : null));
      if (!forkId) {
        setForkedFromAccessible(false);
        return;
      }
      // Check whether the user can actually open the parent (RLS will hide it otherwise)
      const { data: parent } = await supabase
        .from("conversations")
        .select("id")
        .eq("id", forkId)
        .maybeSingle();
      if (cancelled) return;
      setForkedFromAccessible(!!parent);
    })();
    return () => { cancelled = true; };
  }, [activeConversationId]);

  // Clear preview panel when switching conversations
  useEffect(() => {
    setActivePreview(null);
  }, [activeConversationId]);

  // Auto-scroll: only when user is near the bottom, or when streaming just started (user sent message)
  useEffect(() => {
    if (isStreaming && !wasStreaming.current) {
      // Streaming just started — user sent a message, force scroll
      isAtBottom.current = true;
    }
    wasStreaming.current = isStreaming;

    if (isAtBottom.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatMessages, isStreaming]);

  // Reset visible window when switching conversations
  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE);
  }, [activeConversationId]);

  // Scroll to a specific message when navigating from search results.
  // If the target is outside the visible window, expand to include it.
  useEffect(() => {
    if (!scrollToMessageId || !chatMessages.length) return;
    const idx = chatMessages.findIndex((m) => m.id === scrollToMessageId);
    if (idx >= 0) {
      const neededFromEnd = chatMessages.length - idx;
      if (neededFromEnd > visibleCount) {
        setVisibleCount(chatMessages.length);
      }
    }
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-message-id="${scrollToMessageId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-brand-primary', 'rounded-xl');
        setTimeout(() => el.classList.remove('ring-2', 'ring-brand-primary', 'rounded-xl'), 2000);
      }
      setScrollToMessageId(null);
    }, 300);
    return () => clearTimeout(timer);
  }, [scrollToMessageId, chatMessages, visibleCount, setScrollToMessageId]);

  const handleNewConversation = useCallback(async () => {
    // Just clear the active conversation to show the empty/welcome state
    setActiveConversationId(null);
  }, [setActiveConversationId]);

  const handleSendMessage = useCallback(async () => {
    // Guard sincrono: bloqueia double-fire enquanto criamos conversa
    // (isStreaming so vira true depois do sendMessage, deixando uma janela de race)
    if (isSubmittingRef.current) return;

    const trimmed = input.trim();
    if (!trimmed || isStreaming) return;
    if (chatFiles.isProcessing) return; // wait for files to finish

    // Lock + feedback visual imediato (textarea limpa, botao vai pra loading)
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setInput("");
    lastUserSubmitAtRef.current = new Date().toISOString();

    // Priming de notificação: na 1ª vez que o user dispara uma Geração e ainda
    // não respondeu o prompt do browser, oferece ativar (soft prompt). Não
    // dispara o prompt nativo direto — evita "queimar" o tiro único.
    if (!notificationPrimeShown && notificationsSupported() && getNotificationPermission() === "default") {
      notificationPrimeShown = true;
      toast("Ativar notificações?", {
        description: "Avisamos quando uma resposta ficar pronta, mesmo se você estiver em outra aba.",
        action: {
          label: "Ativar",
          onClick: () => {
            void requestNotificationPermission();
          },
        },
        duration: 12000,
      });
    }

    try {
    // Block sync effect BEFORE any state change or DB write to prevent race condition
    // that causes first message duplication (sync adds DB copy before sendMessage adds optimistic copy)
    skipSyncRef.current = true;

    let convId = activeConversationId;

    // Create conversation lazily on first message
    if (!convId) {
      const scope = navigation.projectId ? 'project' : (navigation.isPersonal ? 'personal' : 'global');
      const result = await createConversation.mutateAsync({
        model: selectedModel,
        project_id: navigation.projectId ?? null,
        agent_id: activeAgentId ?? null,
        scope,
      });
      convId = result.id;
      // Update ref immediately so transport.body() picks up the convId
      // (state update via setActiveConversationId is async and may not re-render in time)
      activeConvRef.current = convId;
      setActiveConversationId(convId);
    }

    // Build message content — always keep text extraction for DB persistence / rehydration
    let messageContent = trimmed;
    
    if (chatFiles.hasAttachments) {
      const readyFiles = chatFiles.attachments.filter(a => a.status === 'ready');

      // Send files inline (base64) only for types models handle well AND that are small enough
      // to fit no body da edge function. Áudio NUNCA vai inline — sobe via Storage e a edge
      // baixa server-side (sem o teto de body da edge), permitindo arquivos de centenas de MB.
      // Inline base64 SÓ para tipos leves (imagens, vídeo). PDFs seguem via
      // Storage e a edge baixa server-side — enviar base64 de PDF no body faz
      // o worker do Supabase estourar o teto de memória (WORKER_RESOURCE_LIMIT):
      // um PDF de ~8MB vira ~10MB de base64 no body, +cópias no atob/Uint8Array/
      // reencode do provider = >90MB de pico só pro arquivo. Áudio já usava esse
      // caminho; agora PDF segue o mesmo padrão.
      const NATIVE_INLINE_PREFIXES = ['image/', 'video/'];
      const isInlineMime = (mime: string) => NATIVE_INLINE_PREFIXES.some(p => mime.startsWith(p));
      const MAX_NATIVE_BYTES = 8 * 1024 * 1024; // 8 MB

      const nativeFileAttachments: Array<{ fileName: string; mimeType: string; base64Data: string }> = [];
      const oversizedNative: string[] = [];
      for (const att of readyFiles) {
        const mime = correctMimeType(att.fileName, att.file.type);
        if (!isInlineMime(mime)) continue; // text/PDF/áudio: rehydratados server-side via Storage (chatFileRecords)
        if (att.file.size > MAX_NATIVE_BYTES) {
          // Too large to base64-inline. Sandbox already has it via lazy upload.
          oversizedNative.push(att.fileName);
          continue;
        }
        try {
          const arrayBuffer = await att.file.arrayBuffer();
          const bytes = new Uint8Array(arrayBuffer);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
          }
          const base64 = btoa(binary);
          nativeFileAttachments.push({
            fileName: att.fileName,
            mimeType: mime,
            base64Data: base64,
          });
        } catch (e) {
          console.warn('[ChatPage] Failed to read file as base64:', att.fileName, e);
        }
      }

      // Store in ref so transport.body() can pick them up
      nativeFilesRef.current = nativeFileAttachments;

      if (oversizedNative.length > 0) {
        toast.info(
          `Arquivo(s) grande(s) (${oversizedNative.join(', ')}) ficarão disponíveis apenas via sandbox. O modelo pode usar a ferramenta render_pdf_pages para visualizar páginas específicas.`,
          { duration: 7000 },
        );
      }

      // No XML injection needed — backend handles file rehydration from storage
    }

    // Set refs for server-side persistence (edge function saves user message + chat_files)
    userMessageRef.current = trimmed;
    if (chatFiles.hasAttachments) {
      const readyFiles = chatFiles.attachments.filter(a => a.status === 'ready');
      chatFileRecordsRef.current = readyFiles.map(att => ({
        file_name: att.fileName,
        file_type: att.fileType,
        file_size_bytes: att.file.size,
        mime_type: att.file.type || 'application/octet-stream',
        storage_path: att.storagePath || '',
        content_text: buildChatAttachmentPreview(att.fileName, att.file.type || 'application/octet-stream', att.contentText),
        token_count: att.tokenCount || 0,
      }));
    }

    const hadAttachments = chatFiles.hasAttachments;
    chatFiles.clearAttachments();
    if (hadAttachments) refreshConversationFiles();

    // GERAÇÃO DE IMAGEM: roda no GenerationRegistry (por conversa, sobrevive
    // à troca de conversa/aba). A mensagem do user e o resultado são escritos
    // no Chat desta conversa pelo registry.
    if (isImageGenModel) {
      const agentPrompt = activeAgent?.system_prompt ?? undefined;
      void generation.startImage(convId, {
        prompt: trimmed,
        model: selectedModel,
        ...(agentPrompt ? { systemPrompt: agentPrompt } : {}),
        userText: trimmed,
      });
      return;
    }

    // CHAT DE TEXTO: monta o payload (settings FIXOS — ver ADR 0001) e dispara
    // a Geração no registry. skipSyncRef segue true até status ready/error,
    // evitando que o sync do DB injete mensagens durante o stream.
    const sendBody = buildSendBody(convId);
    generation.startText(convId, {
      body: sendBody,
      userMessage: { role: "user", parts: [{ type: "text", text: messageContent }] },
      seedMessages: [],
    });
    } catch (err) {
      // Libera o lock se algo der errado antes do stream comecar
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      throw err;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    input,
    isStreaming,
    activeConversationId,
    createConversation,
    selectedModel,
    navigation.projectId,
    navigation.isPersonal,
    setActiveConversationId,
    chatFiles,
    isImageGenModel,
    activeAgent,
    generation,
    buildSendBody,
  ]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handleInputPaste = useCallback(
    async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const clipboardItems = Array.from(e.clipboardData?.items ?? []);
      const imageItems = clipboardItems.filter((item) => item.type.startsWith('image/'));

      if (imageItems.length === 0) return;

      e.preventDefault();

      if (!modelSupportsVision) {
        setFileCompatWarning(true);
      }

      for (const [index, item] of imageItems.entries()) {
        const blob = item.getAsFile();
        if (!blob) continue;

        const extension = getImageExtensionFromMime(blob.type);
        const file = new File(
          [blob],
          `imagem-colada-${Date.now()}-${index + 1}.${extension}`,
          { type: blob.type || `image/${extension}` },
        );

        await chatFiles.addFile(file);
      }
    },
    [chatFiles, modelSupportsVision],
  );

  const dragCounterRef = useRef(0);

  useEffect(() => {
    const hasFiles = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes("Files");

    const onDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) setIsDragging(true);
    };

    const onDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };

    const onDragLeave = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) setIsDragging(false);
    };

    const onDrop = async (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);

      const files = Array.from(e.dataTransfer?.files ?? []);
      if (files.length === 0) return;

      const hasImage = files.some((f) => f.type.startsWith("image/"));
      if (hasImage && !modelSupportsVision) {
        setFileCompatWarning(true);
      }

      for (const file of files) {
        await chatFiles.addFile(file);
      }
    };

    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, [chatFiles, modelSupportsVision]);

  const handleStop = useCallback(async () => {
    // Para a Geração desta conversa via registry: aborta o stream do Chat +
    // marca generation_status='cancelled' no DB (o edge function para entre steps).
    if (activeConversationId) {
      await generation.stop(activeConversationId);
    } else {
      stop();
    }
  }, [stop, activeConversationId, generation]);

  const handleFork = useCallback(
    async (messageId: string) => {
      if (!activeConversationId) return;
      const result = await forkConversation.mutateAsync({
        conversationId: activeConversationId,
        forkAtMessageId: messageId,
      });
      setActiveConversationId(result.id);
      toast.success("Conversa bifurcada com sucesso");
    },
    [activeConversationId, forkConversation, toast, setActiveConversationId],
  );
  const handleSummarizeAndFork = useCallback(async () => {
    if (!activeConversationId) return;
    try {
      const result = await summarizeAndFork.mutateAsync({ conversationId: activeConversationId });
      setActiveConversationId(result.id);
      toast.success("Conversa resumida — continuando em nova conversa");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Falha ao resumir a conversa";
      toast.error(message);
    }
  }, [activeConversationId, summarizeAndFork, toast, setActiveConversationId]);
  const handleRetry = useCallback(async (msgId: string) => {
    const msg = chatMessages.find(m => m.id === msgId);
    if (!msg) return;
    const text = getMessageText(msg);
    if (!text) return;

    // Remove failed flag
    setFailedMessageIds(prev => { const next = new Set(prev); next.delete(msgId); return next; });

    // Delete old message from DB and useChat state
    await supabase.from("messages").delete().eq("id", msgId);
    setMessages(chatMessages.filter(m => m.id !== msgId));

    // Re-send
    skipSyncRef.current = true;
    const convId = activeConvRef.current;
    if (!convId) return;
    await supabase.from("messages").insert({
      conversation_id: convId,
      role: "user",
      content: text,
      tokens_used: estimateTokens(text),
    });

    // Re-envia via registry (settings fixos). A mensagem do user já foi
    // inserida no DB acima, então buildSendBody NÃO inclui userMessage e o
    // edge function não a duplica.
    userMessageRef.current = undefined;
    const retryBody = buildSendBody(convId);
    generation.startText(convId, {
      body: retryBody,
      userMessage: { role: "user", parts: [{ type: "text", text }] },
      seedMessages: [],
    });
    setTimeout(() => { skipSyncRef.current = false; }, 2000);
  }, [chatMessages, setMessages, buildSendBody, generation]);

  const handleRemoveFailedMessage = useCallback(async (msgId: string) => {
    const msg = chatMessages.find(m => m.id === msgId);
    const text = msg ? getMessageText(msg) : '';

    setFailedMessageIds(prev => { const next = new Set(prev); next.delete(msgId); return next; });
    setMessages(chatMessages.filter(m => m.id !== msgId));
    await supabase.from("messages").delete().eq("id", msgId);

    if (text) setInput(text);
  }, [chatMessages, setMessages]);

  // Retoma um turno que foi interrompido por timeout/abort/error. Envia
  // uma mensagem do user pedindo pra continuar — o agente recebe o historico
  // completo (incluindo a mensagem partial=false com finish_reason de
  // interrupcao) e segue de onde parou. Usado pelo banner de "Resposta
  // interrompida" renderizado abaixo da ultima mensagem assistant quando
  // o turno fechou anormalmente.
  const handleResumeAfterInterruption = useCallback(async () => {
    if (!activeConversationId || isStreaming) return;
    const resumeText =
      "Continue de onde parou. Não recomece do zero — siga a partir do último passo concluído.";
    skipSyncRef.current = true;
    userMessageRef.current = undefined;
    const resumeBody = buildSendBody(activeConversationId);
    generation.startText(activeConversationId, {
      body: resumeBody,
      userMessage: { role: "user", parts: [{ type: "text", text: resumeText }] },
      seedMessages: [],
    });
    setTimeout(() => {
      skipSyncRef.current = false;
    }, 2000);
  }, [activeConversationId, isStreaming, buildSendBody, generation]);

  const handleCopyMessage = useCallback((id: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedMsgId(id);
    setTimeout(() => setCopiedMsgId(null), 2000);
  }, []);

  // Show typing indicator when waiting for first token (including empty assistant message)
  const lastMsg = chatMessages[chatMessages.length - 1];
  const lastMsgHasText = lastMsg?.role === "assistant" && !!getMessageText(lastMsg);
  const lastMsgHasToolParts = lastMsg?.role === "assistant" && lastMsg.parts?.some((p: any) => p.type === 'tool-invocation' || p.type === 'dynamic-tool' || (typeof p.type === 'string' && p.type.startsWith('tool-')));
  // Show typing indicator throughout the entire generation, so the user always
  // sees activity while the assistant is working (not just before the first token).
  const showTypingIndicator = isStreaming;

  // Detecta se a ultima mensagem do assistant terminou anormalmente (timeout
  // do servidor, abort, erro). Lemos do banco porque o useChat nao expoe
  // metadata.finish_reason no UIMessage. Mostramos um banner "Resposta
  // interrompida" + botao "Continuar de onde parou" pra que o user nao
  // tenha que digitar manualmente.
  const lastInterruption = useMemo(() => {
    if (isStreaming) return null;
    if (!dbMessages || dbMessages.length === 0) return null;
    const lastDbMsg = dbMessages[dbMessages.length - 1] as {
      id: string;
      role: string;
      created_at?: string;
      metadata?: { finish_reason?: string; partial?: boolean; error?: ProviderErrorDetails; started_at?: string };
    } | undefined;
    if (!lastDbMsg || lastDbMsg.role !== "assistant") return null;
    const finishReason = lastDbMsg.metadata?.finish_reason;
    const isPartial = lastDbMsg.metadata?.partial === true;
    const errorDetails = lastDbMsg.metadata?.error;
    if (finishReason === 'timeout' || finishReason === 'aborted' || finishReason === 'error') {
      return {
        reason: finishReason as 'timeout' | 'aborted' | 'error' | 'orphan',
        msgId: lastDbMsg.id as string,
        errorDetails,
        elapsedMs: undefined as number | undefined,
      };
    }
    if (isPartial && !isServerGenerating) {
      // Sem finish_reason classificado: tratamos como orfao. Calculamos quanto
      // tempo a mensagem ficou pendurada pra distinguir entre uma queda recente
      // (segundos) e um zumbi real de longa duracao (minutos).
      const startedAt = lastDbMsg.metadata?.started_at ?? lastDbMsg.created_at;
      const elapsedMs = startedAt ? Math.max(0, Date.now() - new Date(startedAt).getTime()) : undefined;
      const isLong = elapsedMs !== undefined && elapsedMs >= 60_000;
      return {
        reason: (isLong ? 'timeout' : 'orphan') as 'timeout' | 'aborted' | 'error' | 'orphan',
        msgId: lastDbMsg.id as string,
        errorDetails: undefined,
        elapsedMs,
      };
    }
    return null;
  }, [dbMessages, isStreaming, isServerGenerating]);

  const contextUsage = useConversationContextUsage(dbMessages as any, selectedModel);

  // Detect if current model uses OpenRouter native web search (not exposed as tool-invocation)
  const modelSupportsWebSearch = currentModelCaps.web_search === true;
  const showWebSearchIndicator = isStreaming && modelSupportsWebSearch && !lastMsgHasText;

  const isEmptyState = !activeConversationId;

  const greetingBlock = (() => {
    if (activeAgent) {
      return (
        <div className="text-center mb-8 flex flex-col items-center gap-2 w-full max-w-2xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center">
              <Bot className="w-4 h-4 text-brand-primary" />
            </div>
            <h1 className="text-2xl font-semibold text-primary">{activeAgent.name}</h1>
          </div>
          {activeAgent.description && (
            <p className="text-sm text-tertiary w-full">{activeAgent.description}</p>
          )}
        </div>
      );
    }
    const hour = new Date().getHours();
    const firstName =
      user?.user_metadata?.nome?.split(" ")[0] ||
      user?.user_metadata?.full_name?.split(" ")[0] ||
      user?.email?.split("@")[0] ||
      "";
    let greeting: string;
    if (hour >= 22 || hour < 5) greeting = `Trabalhando até tarde, ${firstName}?`;
    else if (hour < 12) greeting = `Bom dia, ${firstName}!`;
    else if (hour < 18) greeting = `Boa tarde, ${firstName}!`;
    else greeting = `Boa noite, ${firstName}!`;
    return (
      <div className="text-center mb-8">
        <h1 style={{ fontFamily: "'Merriweather', Georgia, serif", fontWeight: 300 }} className="text-4xl text-primary">{greeting}</h1>
        <p className="text-sm text-tertiary mt-2">Envie uma mensagem para iniciar uma conversa e VAMO DALE!</p>
      </div>
    );
  })();

  // getAttachmentIcon now defined at module scope


  const inputBlock = (
    <div
      className={`relative ${isEmptyState ? "w-full max-w-2xl" : "w-full max-w-3xl mx-auto max-md:max-w-full max-md:px-4 pt-0 pb-4"}`}
    >
      {/* Attached files preview */}
      {chatFiles.hasAttachments && (
        <div className="flex flex-wrap gap-2 mb-2">
          {chatFiles.attachments.map((att) => (
            <div key={att.id} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary border border-color text-sm">
              {att.status === 'uploading' || att.status === 'parsing' ? (
                <Loader2 className="w-3.5 h-3.5 text-secondary animate-spin" />
              ) : att.status === 'error' ? (
                <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
              ) : (
                getAttachmentIcon(att.fileType)
              )}
              <span className="text-primary truncate max-w-[140px]">{att.fileName}</span>
              {att.status === 'ready' && att.tokenCount && (
                <span className="text-xs text-tertiary">{att.tokenCount.toLocaleString()}t</span>
              )}
              <button
                onClick={() => chatFiles.removeFile(att.id)}
                className="p-0.5 hover:bg-hover rounded"
              >
                <X className="w-3 h-3 text-secondary" />
              </button>
            </div>
          ))}
        </div>
      )}
      {/* File compatibility warning */}
      {fileCompatWarning && chatFiles.hasAttachments && (
        <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-700 dark:text-yellow-400">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="flex-1">O modelo atual não suporta arquivos nativamente. O conteúdo será extraído como texto. Para melhor resultado, troque para um modelo compatível.</span>
          <button
            onClick={() => { setModelSelectorModality('file'); setModelSelectorOpen(true); }}
            className="shrink-0 px-2.5 py-1 rounded-md bg-yellow-500/20 hover:bg-yellow-500/30 font-medium transition-colors"
          >
            Trocar modelo de IA
          </button>
        </div>
      )}
      <div className="relative">
        <textarea
          ref={inputTextareaRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handleInputPaste}
          disabled={isStreaming || isSubmitting}
          placeholder={isDragging ? "" : isSubmitting ? "Enviando..." : "Digite sua mensagem..."}
          rows={1}
          className={`w-full min-h-[56px] max-h-[240px] overflow-y-auto px-4 py-3 rounded-xl text-primary placeholder:text-tertiary focus:outline-none resize-none disabled:opacity-50 transition-colors ${
            isDragging
              ? "border-2 border-dashed border-brand-primary bg-brand-primary/10 focus:ring-0"
              : "border border-color bg-surface focus:ring-2 focus:ring-brand-primary"
          }`}
        />
        {isDragging && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand-primary text-brand-foreground text-sm font-semibold shadow-md">
              <Paperclip className="w-4 h-4" />
              Solta aqui pra anexar
            </div>
          </div>
        )}
      </div>
      <input
        type="file"
        ref={chatFiles.fileInputRef}
        accept={fileAcceptString}
        className="hidden"
        multiple
        onChange={async (e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length > 0) {
            const hasImage = files.some((f) => {
              const ext = f.name.split('.').pop()?.toLowerCase() || '';
              return ['png','jpg','jpeg','gif','webp'].includes(ext);
            });
            if (hasImage) {
              const currentModel = llmModels?.find(m => m.id === selectedModel);
              const caps = currentModel?.capabilities as Record<string, boolean> | undefined;
              if (!caps?.vision) {
                setFileCompatWarning(true);
              }
            }
            for (const f of files) {
              await chatFiles.addFile(f);
            }
          }
          e.target.value = '';
        }}
      />
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          {/* TODO: botão '+' oculto até implementar funcionalidades associadas */}
          <button onClick={chatFiles.openFilePicker} className="p-1.5 hover:bg-hover rounded-md text-secondary" title="Anexar arquivo">
            <Paperclip className="w-4 h-4" />
          </button>
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1.5 hover:bg-hover rounded-md text-secondary">
                  <Download className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => handleExport("html")}>
                  <FileText className="w-4 h-4 mr-2" />
                  Exportar HTML
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport("pdf")}>
                  <FileDown className="w-4 h-4 mr-2" />
                  Exportar PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canShare && (
            <button
              onClick={() => setShareModalOpen(true)}
              className="p-1.5 hover:bg-hover rounded-md text-secondary"
              title="Compartilhar conversa"
            >
              <Share2 className="w-4 h-4" />
            </button>
          )}
          {navigation.projectId && (
            <button onClick={() => setConfigPanelOpen(true)} className="p-1.5 hover:bg-hover rounded-md text-secondary">
              <Settings className="w-4 h-4" />
            </button>
          )}
          {contextUsage?.hasMeasurement && (
            <ConversationContextIndicator
              usage={contextUsage}
              onCompact={handleSummarizeAndFork}
              compactPending={summarizeAndFork.isPending}
            />
          )}
        </div>
        <div className="flex items-center gap-2">
          {activeConversationId && <SandboxStatusBadge userId={user?.id} />}
          <button
            type="button"
            onClick={() => { setModelSelectorModality(undefined); setModelSelectorOpen(true); }}
            disabled={isStreaming}
            className="px-2.5 py-1.5 rounded-md border border-color bg-surface text-xs text-primary flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-brand-primary disabled:opacity-50"
          >
            <span className="truncate max-w-[120px]">
              {llmModels?.find((m) => m.id === selectedModel)?.display_name ?? "Modelo"}
            </span>
            <ChevronDown size={12} className="text-secondary shrink-0" />
          </button>
          <ModelSelectorModal
            open={modelSelectorOpen}
            onOpenChange={(open) => { setModelSelectorOpen(open); if (!open) setModelSelectorModality(undefined); }}
            value={selectedModel}
            onSelect={(id) => {
              userOverrodeModel.current = true;
              setSelectedModel(id);
              // Clear file compat warning if new model supports vision
              const newModel = llmModels?.find(m => m.id === id);
              const newCaps = newModel?.capabilities as Record<string, boolean> | undefined;
              if (newCaps?.vision) {
                setFileCompatWarning(false);
              }
            }}
            requiredModality={modelSelectorModality}
          />

          {isStreaming ? (
            <button onClick={handleStop} className="p-2 bg-destructive text-white rounded-md">
              <Square className="w-4 h-4" />
            </button>
          ) : isSubmitting ? (
            <button disabled className="p-2 bg-brand-primary text-white rounded-md opacity-70 cursor-wait">
              <Loader2 className="w-4 h-4 animate-spin" />
            </button>
          ) : (
            <button
              onClick={handleSendMessage}
              disabled={!input.trim() || chatFiles.isProcessing}
              className="p-2 bg-brand-primary text-white rounded-md disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      <ShareConversationModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        conversationId={activeConversationId}
      />
    </div>
  );

  // Empty state: greeting + input centered vertically, no border
  if (isEmptyState) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-4 md:px-8">
          {greetingBlock}
          {inputBlock}
        </div>
        <ConfigPanel />
      </div>
    );
  }

  // Active conversation: messages scroll under fixed-bottom input
  return (
    <ResizablePanelGroup
      key={activePreview ? "with-preview" : "no-preview"}
      direction="horizontal"
      className="h-full"
    >
      <ResizablePanel defaultSize={activePreview ? 58 : 100} minSize={30}>
      <div className="flex flex-col min-w-0 min-h-0 h-full">
      <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto min-h-0 pt-6 pb-0 flex flex-col items-center">
        <div className="w-full max-w-3xl max-md:max-w-full max-md:px-4 px-0">
        {/* Knowledge base indicator */}
        {navigation.projectId && allReadyProjectFiles.length > 0 && (
          <button
            onClick={() => setConfigPanelOpen(true)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-4 rounded-md text-xs font-medium bg-brand-primary/5 border border-brand-primary/20 text-brand-primary hover:bg-brand-primary/10 transition-colors"
          >
            <BookOpen className="w-3.5 h-3.5" />
            Base de conhecimento: {allReadyProjectFiles.length} {allReadyProjectFiles.length === 1 ? "arquivo" : "arquivos"}
            {totalFileTokens > 0 && ` (${totalFileTokens.toLocaleString()} tokens)`}
          </button>
        )}
        {messagesLoading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 w-3/4 rounded-xl" />
            ))}
          </div>
        ) : (
          <div className="space-y-4 mt-auto">
            {forkedFromId && (
              <div
                className={`flex items-center gap-2 text-xs text-secondary bg-secondary px-3 py-2 rounded-md transition-colors ${forkedFromAccessible ? "cursor-pointer hover:bg-hover" : "cursor-default"}`}
                onClick={forkedFromAccessible ? () => setActiveConversationId(forkedFromId) : undefined}
                title={forkedFromAccessible ? "Abrir conversa original" : "Conversa original não acessível"}
              >
                <GitBranch className="w-3.5 h-3.5" />
                <span>
                  Bifurcada de "<span className="font-medium text-primary">{forkedFromTitle ?? "..."}</span>"
                  {forkedFromOwnerName && (
                    <> por <span className="font-medium text-primary">{forkedFromOwnerName}</span></>
                  )}
                </span>
              </div>
            )}

            {chatMessages.length === 0 && !isStreaming ? (
              <div className="flex flex-col items-center justify-center h-full text-center py-20">
                <p className="text-sm text-tertiary">Inicie a conversa enviando uma mensagem.</p>
              </div>
            ) : (
              <TooltipProvider>
                {(() => {
                  const totalCount = chatMessages.length;
                  const startIdx = Math.max(0, totalCount - visibleCount);
                  const hiddenCount = startIdx;
                  const visibleSlice = chatMessages.slice(startIdx);
                  return (
                    <>
                      {hiddenCount > 0 && (
                        <div className="flex justify-center py-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              if (e.shiftKey) setVisibleCount(totalCount);
                              else setVisibleCount((c) => c + 15);
                            }}
                            className="px-3 py-1.5 rounded-md text-xs text-secondary hover:text-primary hover:bg-hover border border-color transition-colors"
                            title="Shift+clique para mostrar tudo"
                          >
                            Mostrar mensagens anteriores ({hiddenCount})
                          </button>
                        </div>
                      )}
                      {visibleSlice.map((msg, sliceIdx) => {
                        const idx = startIdx + sliceIdx;
                        const isStreamingMsg =
                          isStreaming && msg === chatMessages[totalCount - 1] && msg.role === "assistant";
                        const msgFiles = conversationFiles[msg.id];
                        const isFailed = failedMessageIds.has(msg.id);
                        const isAssistant = msg.role === "assistant";
                        const prevMsg = idx > 0 ? chatMessages[idx - 1] : undefined;
                        const prevCreatedAt = prevMsg ? messageTimestamps.get(prevMsg.id) : undefined;
                        // rangeEnd = created_at do PROXIMO assistant (nao da proxima mensagem qualquer).
                        // Assim, arquivos que sobem tarde (upload demora apos texto streamar)
                        // caem na janela do proximo turno do assistant em vez de sumir quando
                        // o usuario responde rapido. Mensagens de user nao geram arquivos.
                        let nextAssistantCreatedAt: string | undefined;
                        for (let j = idx + 1; j < totalCount; j++) {
                          if (chatMessages[j].role === "assistant") {
                            nextAssistantCreatedAt = messageTimestamps.get(chatMessages[j].id);
                            break;
                          }
                        }
                        const ownCreatedAt = messageTimestamps.get(msg.id);
                        // Durante streaming, mensagens podem ter ids locais do useChat sem created_at
                        // hidratado do DB. Para o ultimo bubble assistant, usa o timestamp de submit
                        // como fallback para nao esconder o painel de artefatos.
                        const isLastAssistant = isAssistant && idx === totalCount - 1;
                        const fallbackStart = isLastAssistant ? lastUserSubmitAtRef.current ?? undefined : undefined;
                        const rangeStart = isAssistant ? (prevCreatedAt ?? ownCreatedAt ?? fallbackStart) : undefined;
                        const rangeEnd = isAssistant ? nextAssistantCreatedAt : undefined;
                        const showInlineArtifacts = isAssistant && !!rangeStart;
                        return (
                          <MessageBubble
                            key={msg.id}
                            msg={msg}
                            isStreamingMsg={isStreamingMsg}
                            isFailed={isFailed}
                            msgFiles={msgFiles}
                            showInlineArtifacts={showInlineArtifacts}
                            rangeStart={rangeStart}
                            rangeEnd={rangeEnd}
                            conversationId={activeConversationId ?? undefined}
                            userId={user?.id}
                            copiedMsgId={copiedMsgId}
                            forkPending={forkConversation.isPending}
                            onPreview={setActivePreview}
                            onCopy={handleCopyMessage}
                            onFork={handleFork}
                            onRetry={handleRetry}
                            onRemove={handleRemoveFailedMessage}
                          />
                        );
                      })}
                    </>
                  );
                })()}
              </TooltipProvider>
            )}

            {/* Banner de resposta interrompida — turno terminou anormalmente
                (timeout do servidor / abort / erro). Ofeece retomada com 1
                clique sem o user precisar redigitar pedido. Soluciona Bug 3
                em conjunto com o fix server-side em supabase/functions/chat. */}
            {lastInterruption && !isStreaming && (
              <div className="flex justify-start">
                <div className="w-full max-w-2xl rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-3">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      {(() => {
                        const modelLabel = llmModels?.find(m => m.id === selectedModel)?.display_name;
                        if (lastInterruption.errorDetails) {
                          const { title, description } = friendlyProviderError(lastInterruption.errorDetails, modelLabel);
                          return (
                            <>
                              <p className="text-sm font-medium text-primary">{title}</p>
                              {description && <p className="text-xs text-secondary mt-1">{description}</p>}
                            </>
                          );
                        }
                        const minutes = lastInterruption.elapsedMs
                          ? Math.max(1, Math.round(lastInterruption.elapsedMs / 60_000))
                          : null;
                        const title =
                          lastInterruption.reason === 'timeout'
                            ? 'Resposta interrompida por limite de tempo'
                            : lastInterruption.reason === 'aborted'
                            ? 'Resposta cancelada'
                            : lastInterruption.reason === 'orphan'
                            ? 'Resposta interrompida inesperadamente'
                            : 'Resposta interrompida por erro';
                        const desc =
                          lastInterruption.reason === 'timeout'
                            ? `O turno passou de ${minutes ?? '6'} min sem completar e foi encerrado pelo servidor. O trabalho até aqui está salvo. Posso continuar de onde parei.`
                            : lastInterruption.reason === 'aborted'
                            ? 'O turno foi cancelado antes de terminar. O trabalho até aqui está salvo.'
                            : lastInterruption.reason === 'orphan'
                            ? 'A conexão com o provedor do modelo caiu antes de finalizar a resposta. O trecho gerado até aqui está salvo. Tente continuar de onde parou ou refaça o pedido.'
                            : 'O turno terminou por erro. O trabalho até aqui está salvo. Posso tentar continuar.';
                        return (
                          <>
                            <p className="text-sm font-medium text-primary">{title}</p>
                            <p className="text-xs text-secondary mt-1">{desc}</p>
                          </>
                        );
                      })()}
                      <div className="mt-2 flex gap-2">
                        <button
                          type="button"
                          onClick={handleResumeAfterInterruption}
                          disabled={isStreaming}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-brand-primary text-white hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Continuar de onde parou
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Web search indicator (OpenRouter native — not exposed as tool parts) */}
            {showWebSearchIndicator && !showTypingIndicator && (
              <div className="flex justify-start">
                <div className="rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2 text-xs text-tertiary">
                    <span className="flex items-center justify-center w-5 h-5 rounded-full bg-secondary">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    </span>
                    <Globe className="w-3.5 h-3.5" />
                    <span>Pesquisando na web…</span>
                  </div>
                </div>
              </div>
            )}

            {showTypingIndicator && (
              <div className="flex justify-start">
                <div className="rounded-xl px-4 py-3">
                  {isGeneratingImage ? (
                    <div className="flex items-center gap-2 text-sm text-secondary">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {imageGenStatus}
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'hsl(var(--text-tertiary))', animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'hsl(var(--text-tertiary))', animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: 'hsl(var(--text-tertiary))', animationDelay: '300ms' }} />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
        </div>
      </div>

      {/* Token limit warning */}
      {navigation.projectId && totalFileTokens > TOKEN_LIMIT_WARNING && (
        <div className="shrink-0 flex items-center gap-2 px-6 py-2 bg-yellow-500/10 border-t border-yellow-500/30 text-yellow-700 dark:text-yellow-400 text-xs">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>
            A base de conhecimento deste projeto é grande e pode não caber no contexto do modelo atual. Considere
            reduzir os arquivos ou usar um modelo com contexto maior.
          </span>
        </div>
      )}

      {/* Input bar at bottom */}
      <div className="shrink-0 relative z-10 bg-surface">{inputBlock}</div>

      <ConfigPanel />
      </div>
      </ResizablePanel>
      {activePreview && (
        <>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={42} minSize={25}>
            {activePreview.kind === 'markdown' ? (
              <MarkdownPreviewPanel
                url={activePreview.url}
                name={activePreview.name}
                onClose={() => setActivePreview(null)}
              />
            ) : (
              <HtmlPreviewPanel
                url={activePreview.url}
                name={activePreview.name}
                onClose={() => setActivePreview(null)}
              />
            )}
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );
};
